import './index.css'

import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { loadArtistShowcases } from '@disco/state-management/actions/artist-actions'
import { getArtistShowcases } from '@disco/state-management/selectors/artists'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'

import type { Artist, ArtistId } from 'types/artist'
import type { ClientLibraryArtist } from 'types/client-library'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'

import example1x from 'assets/images/example-showcase.jpg'
import example2x from 'assets/images/example-showcase@2x.jpg'

import Row from 'components/common/row'
import Flex from 'components/ui/flex'
import Heading from 'components/ui/heading'
import InlineLink from 'components/ui/inline-link'

import { useDispatch, useSelector } from 'hooks/use-redux'

import ArtistShowcasesPlaylist from './ArtistShowcasesPlaylist'
import CreateShowcaseButton from './CreateShowcaseButton'

const CREATE_SHOWCASE_TRACKS_LIMIT = 200
type Props = {
  artist: Artist | ClientLibraryArtist
}

const ArtistShowcases = ({ artist }: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const typeData = useComposerInsteadOfArtist().value
  const showcases: Playlist[] = useSelector((state: State) =>
    getArtistShowcases(state, artist.id),
  )
  const loadData = useCallback(
    async (artistId: ArtistId) => {
      setIsLoading(true)

      try {
        await dispatch(loadArtistShowcases(artistId))
      } catch (error) {}

      setIsLoading(false)
    },
    [dispatch],
  )
  useEffect(() => {
    if (artist.id) {
      loadData(artist.id)
    }
  }, [artist.id])
  const createShowcase = useCallback(() => {
    dispatch(sendMixpanelEvent('Manage Artist modal - Generate showcase'))
    navigate('/')
  }, [dispatch, navigate])
  return (
    <>
      <Heading size={500}>Generate Page</Heading>
      <Row columnsSize={[12]}>
        <div>
          <Flex>
            <div className="form-note">
              To create a new page for this {typeData}
            </div>
            <div>
              <CreateShowcaseButton
                artistId={artist.id}
                disabled={
                  artist.loading ||
                  artist.track_count > CREATE_SHOWCASE_TRACKS_LIMIT
                }
                onClick={createShowcase}
              />
            </div>
          </Flex>
        </div>
      </Row>
      <Row columnsSize={[12]}>
        {artist.showcase_playlist_exists ? (
          <div>
            <Heading size={500}>Pages</Heading>
            {isLoading ? (
              <ArtistShowcasesPlaylist playlist={null} />
            ) : (
              showcases.map((playlist: Playlist) => (
                <ArtistShowcasesPlaylist
                  key={playlist.playlist_id}
                  playlist={playlist}
                />
              ))
            )}
          </div>
        ) : (
          <div>
            <div className="form-note">
              Find out more about creating Artist and Album Pages{' '}
              <InlineLink
                to="https://support.disco.ac/home/albums-pages-guide"
                rel="noopener noreferrer"
                target="_blank"
              >
                here.
              </InlineLink>
            </div>
            <div className="ArtistShowcases-example">
              <img
                srcSet={`${example1x}, ${example2x} 2x`}
                width="180"
                height="317"
              />
            </div>
          </div>
        )}
      </Row>
    </>
  )
}

export default ArtistShowcases
